import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { authService } from '@/services/authService'
import { LoginRequest } from '@/types/auth'
import { Roles } from '@/types/role'
import { useAuth } from '@/stores/authStore'

export function useLogin() {
  const { setUser, setTokens, reset } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true)
      const response = await authService.login(credentials)

      if (response.access_token && response.user.role?.[0] !== Roles.ADMIN) {
        reset()
        return {
          message: 'Oops! Your account doesn’t have admin permissions yet',
          success: false,
        }
      }
      if (response.access_token && response.user.role?.[0] === Roles.ADMIN) {
        const { access_token, refresh_token, user } = response
        setUser(user)
        setTokens(access_token, refresh_token)
        return {
          message: 'Login succesfully',
          success: true,
        }
      }
      reset()
      return {
        message: response.message,
        success: false,
      }
    } catch {
      reset()
      return {
        message: 'Oops! Your account doesn’t have admin permissions yet',
        success: false,
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    login,
    isLoading,
  }
}

export function useLogout() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const logout = async () => {
    try {
      setIsLoading(true)

      await authService.logout()
      navigate({
        to: '/sign-in',
        search: { redirect: undefined },
        replace: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    logout,
    isLoading,
  }
}
export function useAutoRestoreUser() {
  const { accessToken, user, restoreUser } = useAuth()
  const [isRestoring, setIsRestoring] = useState(false)

  useEffect(() => {
    const checkAndRestoreUser = async () => {
      // Only restore if we have accessToken but no user
      if (accessToken && !user && !isRestoring) {
        setIsRestoring(true)
        try {
          await restoreUser()
        } catch (_error) {
          // Auto restore user failed silently
        } finally {
          setIsRestoring(false)
        }
      }
    }

    checkAndRestoreUser()
  }, [accessToken, user, restoreUser, isRestoring])

  return {
    isRestoring,
  }
}
